/**
 * A CHOSEN FILE'S LENGTH AND SHAPE, READ BEFORE IT IS UPLOADED (v1.0.29). The media worker refuses a
 * video by its length and its picture only once the whole file is in - a 25-minute recording is
 * 2.5 GB sent for nothing. The browser can say both from the file's first bytes: a `<video>` with
 * `preload="metadata"` over a local address. So the cabinet asks first, and refuses with the
 * worker's own codes and words.
 *
 * THE LIMITS ARE THE WORKER'S, mirrored and not invented: `judgeVideo` in
 * src/modules/media/video-probe.ts, its order, its one second of slack and its 4% either side of
 * 16:9. A file the browser cannot read - an HEVC `.mov` in a browser without HEVC, say - is let
 * through, and the worker decides; so is any part of it that was not read.
 */
import { createContext, useContext } from 'react';

/** What the browser read of a file; a part it could not read is null. */
export interface VideoMetadata {
  readonly durationMs: number | null;
  /** As displayed: the browser turns a phone's portrait clip upright, as the worker does. */
  readonly widthPx: number | null;
  readonly heightPx: number | null;
}

/** Reads a file's metadata; null when nothing could be read. */
export type VideoMetadataReader = (file: File) => Promise<VideoMetadata | null>;

/** The worker's refusals that a file's metadata already decides (`VideoRefusal`). */
export type VideoShapeRefusal =
  'too_short' | 'too_long' | 'wrong_shape' | 'too_small' | 'too_large';

export const VIDEO_MIN_DURATION_MS = 30_000;
export const VIDEO_MAX_DURATION_MS = 10 * 60_000;
/** A recording stopped at 10:00 runs a few frames over; the worker keeps one second of slack. */
const DURATION_SLACK_MS = 1_000;
export const VIDEO_MIN_SHORT_SIDE = 720;
/** 4K. */
export const VIDEO_MAX_SHORT_SIDE = 2160;
/** 1920x1080 and 1920x1088 alike: 4% either side of 16:9 (or 9:16). */
const SHAPE_TOLERANCE = 0.04;

/** How long the browser is given to read a file's metadata before the upload goes anyway. */
export const METADATA_READ_MS = 5_000;

function shapeFits(width: number, height: number): boolean {
  const ratio = width / height;
  return (
    Math.abs(ratio / (16 / 9) - 1) <= SHAPE_TOLERANCE ||
    Math.abs(ratio / (9 / 16) - 1) <= SHAPE_TOLERANCE
  );
}

/** The worker's verdict on what was read, in its order; null when nothing read refuses the file. */
export function judgeVideoMetadata(metadata: VideoMetadata): VideoShapeRefusal | null {
  const { durationMs, widthPx, heightPx } = metadata;
  if (durationMs !== null) {
    if (durationMs < VIDEO_MIN_DURATION_MS) {
      return 'too_short';
    }
    if (durationMs > VIDEO_MAX_DURATION_MS + DURATION_SLACK_MS) {
      return 'too_long';
    }
  }
  if (widthPx === null || heightPx === null) {
    return null;
  }
  if (!shapeFits(widthPx, heightPx)) {
    return 'wrong_shape';
  }
  const shortSide = Math.min(widthPx, heightPx);
  if (shortSide < VIDEO_MIN_SHORT_SIDE) {
    return 'too_small';
  }
  if (shortSide > VIDEO_MAX_SHORT_SIDE) {
    return 'too_large';
  }
  return null;
}

function positive(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The browser's reader: a detached `<video preload="metadata">` over `URL.createObjectURL(file)`,
 * the address revoked once it has answered, failed or run out of time. Without object addresses
 * (a test's DOM) it reads nothing.
 */
export const readVideoMetadata: VideoMetadataReader = (file) => {
  if (typeof URL.createObjectURL !== 'function') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const address = URL.createObjectURL(file);
    const player = document.createElement('video');
    let done = false;
    const finish = (answer: VideoMetadata | null): void => {
      if (done) {
        return;
      }
      done = true;
      // Never before `timer` exists: every caller is a later event or the timer itself.
      clearTimeout(timer);
      // Let go of the file: the address and the element's hold on it.
      player.removeAttribute('src');
      player.load();
      URL.revokeObjectURL(address);
      resolve(answer);
    };
    const timer = setTimeout(() => {
      finish(null);
    }, METADATA_READ_MS);
    player.preload = 'metadata';
    player.muted = true;
    player.addEventListener('loadedmetadata', () => {
      const seconds = positive(player.duration);
      const width = positive(player.videoWidth);
      const height = positive(player.videoHeight);
      finish(
        seconds === null && (width === null || height === null)
          ? null
          : {
              durationMs: seconds === null ? null : Math.round(seconds * 1000),
              widthPx: width === null || height === null ? null : width,
              heightPx: width === null || height === null ? null : height,
            },
      );
    });
    player.addEventListener('error', () => {
      finish(null);
    });
    player.src = address;
  });
};

const ReaderContext = createContext<VideoMetadataReader>(readVideoMetadata);

/** The video editor provides its reader here; a spec hands in one that answers what it needs. */
export const VideoMetadataReaderProvider = ReaderContext.Provider;

export function useVideoMetadataReader(): VideoMetadataReader {
  return useContext(ReaderContext);
}
